import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import BusinessClock from '../components/BusinessClock';
import { toast } from 'sonner';
import { formatBusinessDate } from '../lib/dateTime';
import {
  TrendingUp, TrendingDown, DollarSign, Users, AlertTriangle,
  Calendar, Target, Shield, Loader2, ArrowUpRight, ArrowDownRight,
  Home, Receipt, CreditCard
} from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, PieChart, Pie, Cell
} from 'recharts';

const DashboardPage = () => {
  const { api, businessSettings, isOwner, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState(null);
  const [salesData, setSalesData] = useState([]);
  const [dashboardData, setDashboardData] = useState(null);
  const [seedingData, setSeedingData] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [metricsRes, salesRes, dashboardRes] = await Promise.all([
        api.get('/reports/survival-metrics'),
        api.get('/reports/daily-sales?days=7'),
        api.get('/reports/sales?range=last_7_days'),
      ]);
      setMetrics(metricsRes.data);
      setSalesData(salesRes.data.reverse());
      setDashboardData(dashboardRes.data);
    } catch (error) {
      if (error.response?.status !== 400) {
        toast.error('Failed to load dashboard data');
      }
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    if (isOwner) {
      fetchData();
    } else {
      setLoading(false);
    }
  }, [fetchData, isOwner]);

  useEffect(() => {
    const refreshDashboard = () => {
      if (isOwner) fetchData();
    };

    window.addEventListener('survipos:dashboard-refresh', refreshDashboard);
    return () => window.removeEventListener('survipos:dashboard-refresh', refreshDashboard);
  }, [fetchData, isOwner]);

  const handleSeedData = async () => {
    setSeedingData(true);
    try {
      await api.post('/seed-data');
      toast.success('Demo data added successfully!');
      fetchData();
    } catch (error) {
      toast.error('Failed to seed data');
    } finally {
      setSeedingData(false);
    }
  };

  const formatCurrency = (amount) => {
    if (amount === undefined || amount === null) return 'UGX 0';
    return `UGX ${amount.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  };

  const getSurvivalColor = (score) => {
    if (score >= 80) return 'text-primary';
    if (score >= 35) return 'text-warning';
    return 'text-destructive';
  };

  const getSurvivalLabel = (score, status) => {
    if (status) return status;
    if (score >= 80) return 'Healthy';
    if (score >= 60) return 'Stable';
    if (score >= 35) return 'At Risk';
    return 'Critical';
  };

  const getStatusBadgeVariant = (status, score) => {
    const resolved = status || getSurvivalLabel(score, status);
    if (resolved === 'Healthy') return 'default';
    if (resolved === 'Stable') return 'warning';
    return 'destructive';
  };

  const formatPercent = (value) => {
    const numeric = Number(value ?? 0);
    const normalized = numeric >= 0 && numeric <= 1 ? numeric * 100 : numeric;
    return normalized.toFixed(1);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isOwner) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center p-8">
        <Shield className="h-16 w-16 text-muted-foreground mb-4" />
        <h2 className="font-heading text-2xl font-semibold mb-2">Owner Access Required</h2>
        <p className="text-muted-foreground max-w-md">
          The survival dashboard is only available to business owners. 
          Please use the POS screen to process sales.
        </p>
      </div>
    );
  }

  if (!metrics) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center p-8">
        <Target className="h-16 w-16 text-muted-foreground mb-4" />
        <h2 className="font-heading text-2xl font-semibold mb-2">No Data Yet</h2>
        <p className="text-muted-foreground max-w-md mb-6">
          Start making sales to see your survival metrics, or add demo data to explore the dashboard.
        </p>
        <Button onClick={handleSeedData} disabled={seedingData} data-testid="seed-data-btn">
          {seedingData ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Adding Demo Data...
            </>
          ) : (
            'Add Demo Products & Customers'
          )}
        </Button>
      </div>
    );
  }

  const COLORS = [
    'hsl(var(--chart-1))',
    'hsl(var(--chart-2))',
    'hsl(var(--chart-3))',
    'hsl(var(--chart-4))',
  ];

  const requiredMethods = ['Cash', 'Mobile Money', 'Card', 'Credit'];
  const paymentBreakdown = dashboardData?.payment_methods;
  const fallbackMethods = [
    { name: 'Cash', total: Number(paymentBreakdown?.cash_total || 0), percentage: Number(paymentBreakdown?.cash_percent || 0) },
    { name: 'Mobile Money', total: Number(paymentBreakdown?.mobile_money_total || 0), percentage: Number(paymentBreakdown?.mobile_money_percent || 0) },
    { name: 'Card', total: Number(paymentBreakdown?.card_total || 0), percentage: Number(paymentBreakdown?.card_percent || 0) },
    { name: 'Credit', total: Number(paymentBreakdown?.credit_total || 0), percentage: Number(paymentBreakdown?.credit_percent || 0) },
  ];
  const apiMethods = dashboardData?.paymentMethods?.length ? dashboardData.paymentMethods : fallbackMethods;
  const paymentChartData = requiredMethods.map((name) => {
    const existing = apiMethods.find(
      (item) => item.name?.toLowerCase() === name.toLowerCase()
    );

    return {
      name,
      total: Number(existing?.total || 0),
      percentage: Number(existing?.percentage || 0),
    };
  });
  const paymentDistribution = paymentChartData.map((item) => ({ ...item, value: item.total }));
  const paymentTotal = paymentDistribution.reduce((sum, item) => sum + item.value, 0);
  const survivalStatus = metrics?.survival_status || getSurvivalLabel(metrics?.survival_score ?? 0, metrics?.survival_status);
  const survivalTips = [];
  const coveragePercent = Number(formatPercent(metrics?.rent_coverage_percentage || 0));
  const firstName = user?.full_name?.trim()?.split(/\s+/)?.[0] || businessSettings?.business_name || 'there';

  if (metrics?.rent_risk_override) {
    survivalTips.push({
      id: 'rent-risk',
      tone: 'critical',
      title: 'Urgent Rent Risk',
      description: `Rent is due in ${Math.max(0, Math.trunc(metrics?.days_until_rent_due || 0))} day(s) with only ${coveragePercent.toFixed(1)}% covered. Prioritize rent funding immediately.`,
    });
  }

  if (survivalStatus === 'Critical' || survivalStatus === 'At Risk') {
    survivalTips.push({
      id: 'status-risk',
      tone: 'warning',
      title: 'Stabilize Business Health',
      description: 'Improve fixed obligation readiness, strengthen 7-day sales consistency, and build liquidity buffer.',
    });
  }

  if (metrics?.today_sales < metrics?.daily_break_even) {
    survivalTips.push({
      id: 'sales-target',
      tone: 'critical',
      title: 'Sales Below Target',
      description: `You need ${formatCurrency(metrics.daily_break_even - metrics.today_sales)} more today to cover daily fixed costs.`,
    });
  }

  if (metrics?.overdue_debt > 0) {
    survivalTips.push({
      id: 'overdue-debt',
      tone: 'critical',
      title: 'Overdue Debts',
      description: `${formatCurrency(metrics.overdue_debt)} is overdue. Follow up collections to reduce cashflow pressure.`,
    });
  }

  if (metrics?.cash_vs_credit_ratio < 2) {
    survivalTips.push({
      id: 'credit-mix',
      tone: 'warning',
      title: 'High Credit Sales Mix',
      description: 'Cash-to-credit ratio is low. Push faster collections and prioritize cash/mobile/card payments.',
    });
  }

  if (Array.isArray(metrics?.survival_reasons)) {
    if (metrics.survival_reasons.some((r) => r.toLowerCase().includes('debt data unavailable'))) {
      survivalTips.push({
        id: 'missing-debt-data',
        tone: 'neutral',
        title: 'Debt Data Missing',
        description: 'Add debt records to improve risk accuracy and recommendations.',
      });
    }
    if (metrics.survival_reasons.some((r) => r.toLowerCase().includes('cash data unavailable'))) {
      survivalTips.push({
        id: 'missing-cash-data',
        tone: 'neutral',
        title: 'Cash Data Missing',
        description: 'Cash position is missing, so liquidity risk may be understated.',
      });
    }
  }

  if (survivalStatus === 'Healthy') {
    survivalTips.push({
      id: 'healthy',
      tone: 'positive',
      title: 'Business is Healthy',
      description: 'Keep protecting consistency in sales and maintain rent/debt coverage discipline.',
    });
  }

  if (survivalTips.length === 0) {
    survivalTips.push({
      id: 'no-immediate-risks',
      tone: 'neutral',
      title: 'No Immediate Survival Risks',
      description: 'Keep monitoring daily sales, rent coverage, and debt collection to stay stable.',
    });
  }

  return (
    <div className="space-y-5 animate-fade-in md:space-y-6" data-testid="dashboard-page">
      {/* Header */}
      <div className="space-y-4">
        <div className="space-y-2">
          <h1 className="hidden font-heading text-3xl font-bold md:block">Survival Dashboard</h1>
          <p className="font-heading text-2xl font-bold text-foreground md:text-lg">
            Hello, {firstName} 👋
          </p>
          <p className="text-base text-muted-foreground md:text-sm">
            Here's what's happening with your business today.
          </p>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-[minmax(0,1fr)_auto] md:flex md:items-end md:justify-between">
          <BusinessClock className="w-full justify-between rounded-2xl bg-card px-4 py-3 shadow-[0_18px_44px_-28px_rgba(15,23,42,0.22)] md:w-auto md:justify-start md:rounded-lg md:px-4 md:py-2" />
          <div className="flex justify-start md:justify-end md:self-end">
            <Badge variant={getStatusBadgeVariant(metrics.survival_status, metrics.survival_score)} className="min-h-[56px] w-full justify-center rounded-2xl px-5 py-3 text-base sm:w-auto md:min-h-0 md:rounded-lg md:px-4 md:py-2 md:text-lg">
              Survival Score: {metrics.survival_score}/100
            </Badge>
          </div>
        </div>
      </div>

      {/* Survival Score Card */}
      <Card className="survival-glow overflow-hidden rounded-[28px] shadow-lg md:rounded-xl">
        <CardContent className="pt-6">
          <div className="flex flex-col gap-5 md:flex-row md:items-center md:gap-6">
            <div className="flex-1">
              <h3 className="mb-3 font-heading text-2xl font-semibold md:text-xl">Business Health</h3>
              <div className="mb-4 flex items-center gap-4">
                <span className={`text-6xl font-bold font-mono md:text-5xl ${getSurvivalColor(metrics.survival_score)}`}>
                  {metrics.survival_score}
                </span>
                <div className="space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant={getStatusBadgeVariant(metrics.survival_status, metrics.survival_score)}>
                      {getSurvivalLabel(metrics.survival_score, metrics.survival_status)}
                    </Badge>
                    {metrics.rent_risk_override && (
                      <Badge variant="destructive">Rent Risk</Badge>
                    )}
                  </div>
                  {metrics.rent_risk_override && (
                    <p className="text-xs text-muted-foreground mt-2">
                      Status impacted by upcoming rent and coverage.
                    </p>
                  )}
                  {!metrics.rent_risk_override && metrics.survival_reasons?.length > 0 && (
                    <p className="text-xs text-muted-foreground mt-2">
                      {metrics.survival_reasons[0]}
                    </p>
                  )}
                  <p className="text-sm text-muted-foreground">out of 100 points</p>
                </div>
              </div>
              <Progress value={metrics.survival_score} className="h-4 rounded-full bg-primary/15" />
            </div>
            <div className="grid grid-cols-2 gap-3 md:gap-4">
              <div className="rounded-2xl bg-background/75 p-4 text-center shadow-sm md:rounded-lg">
                <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-destructive/10">
                  <Home className="h-5 w-5 text-destructive" />
                </div>
                <p className="text-sm text-muted-foreground">Days to Rent</p>
                <p className="text-3xl font-bold font-mono md:text-2xl">{Math.max(0, Math.trunc(metrics.days_until_rent_due || 0))}</p>
              </div>
              <div className="rounded-2xl bg-background/75 p-4 text-center shadow-sm md:rounded-lg">
                <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-primary/10">
                  <Target className="h-5 w-5 text-primary" />
                </div>
                <p className="text-sm text-muted-foreground">Rent Coverage</p>
                <p className="text-3xl font-bold font-mono md:text-2xl">{formatPercent(metrics.rent_coverage_percentage)}%</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Key Metrics */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-2 md:gap-4 lg:grid-cols-4">
        <Card className="stat-card">
          <CardHeader className="px-4 pb-2 pt-4 md:px-6 md:pt-6">
            <CardDescription className="flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              Today's Sales
            </CardDescription>
          </CardHeader>
          <CardContent className="px-4 pb-4 md:px-6 md:pb-6">
            <p className="text-2xl font-bold font-mono text-primary md:text-3xl">
              {formatCurrency(metrics.today_sales)}
            </p>
            <div className="flex items-center gap-2 mt-2">
              {metrics.today_sales >= metrics.daily_break_even ? (
                <>
                  <ArrowUpRight className="h-4 w-4 text-primary" />
                  <span className="text-sm text-primary">Above break-even</span>
                </>
              ) : (
                <>
                  <ArrowDownRight className="h-4 w-4 text-destructive" />
                  <span className="text-sm text-destructive">
                    {formatCurrency(metrics.daily_break_even - metrics.today_sales)} to go
                  </span>
                </>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card">
          <CardHeader className="px-4 pb-2 pt-4 md:px-6 md:pt-6">
            <CardDescription className="flex items-center gap-2">
              <Target className="h-4 w-4" />
              Daily Break-Even
            </CardDescription>
          </CardHeader>
          <CardContent className="px-4 pb-4 md:px-6 md:pb-6">
            <p className="text-2xl font-bold font-mono md:text-3xl">
              {formatCurrency(metrics.daily_break_even)}
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              Min. daily sales needed
            </p>
          </CardContent>
        </Card>

        <Card className={`stat-card ${metrics.total_debt_exposure > 0 ? 'debt-glow border-destructive/30' : ''}`}>
          <CardHeader className="px-4 pb-2 pt-4 md:px-6 md:pt-6">
            <CardDescription className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Total Debt Exposure
            </CardDescription>
          </CardHeader>
          <CardContent className="px-4 pb-4 md:px-6 md:pb-6">
            <p className={`text-2xl font-bold font-mono md:text-3xl ${metrics.total_debt_exposure > 0 ? 'text-destructive' : ''}`}>
              {formatCurrency(metrics.total_debt_exposure)}
            </p>
            {metrics.overdue_debt > 0 && (
              <div className="flex items-center gap-2 mt-2">
                <AlertTriangle className="h-4 w-4 text-destructive" />
                <span className="text-sm text-destructive">
                  {formatCurrency(metrics.overdue_debt)} overdue
                </span>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="stat-card">
          <CardHeader className="px-4 pb-2 pt-4 md:px-6 md:pt-6">
            <CardDescription className="flex items-center gap-2">
              <Receipt className="h-4 w-4" />
              {businessSettings?.tax_type === 'vat_registered' ? 'VAT Liability' : 'Presumptive Tax'}
            </CardDescription>
          </CardHeader>
          <CardContent className="px-4 pb-4 md:px-6 md:pb-6">
            <p className="text-2xl font-bold font-mono text-secondary md:text-3xl">
              {formatCurrency(businessSettings?.tax_type === 'vat_registered' ? metrics.vat_liability : metrics.presumptive_tax_estimate)}
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              {businessSettings?.tax_type === 'vat_registered' ? 'This month' : 'Annual estimate'}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Sales Trend */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="font-heading">Sales Trend (Last 7 Days)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              {salesData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={salesData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis 
                      dataKey="date" 
                      stroke="hsl(var(--muted-foreground))"
                      tick={{ fill: 'hsl(var(--muted-foreground))' }}
                      tickFormatter={(value) => formatBusinessDate(value, { weekday: 'short' })}
                    />
                    <YAxis 
                      stroke="hsl(var(--muted-foreground))"
                      tick={{ fill: 'hsl(var(--muted-foreground))' }}
                      tickFormatter={(value) => `${(value / 1000).toFixed(0)}k`}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'hsl(var(--card))', 
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }}
                      formatter={(value) => [formatCurrency(value), 'Sales']}
                      labelFormatter={(value) => formatBusinessDate(value)}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="total_sales" 
                      stroke="hsl(var(--primary))" 
                      strokeWidth={3}
                      dot={{ fill: 'hsl(var(--primary))' }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-muted-foreground">
                  No sales data yet
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Payment Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading">Payment Methods</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[240px] sm:h-[260px]">
              {paymentTotal > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={paymentDistribution}
                      cx="50%"
                      cy="50%"
                      innerRadius={48}
                      outerRadius={80}
                      paddingAngle={3}
                      dataKey="value"
                      label={false}
                      labelLine={false}
                    >
                      {paymentDistribution.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'hsl(var(--card))', 
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px',
                        color: 'hsl(var(--foreground))',
                        fontSize: '12px',
                        padding: '8px 10px'
                      }}
                      formatter={(value, name) => {
                        const numericValue = Number(value ?? 0);
                        const percent = paymentTotal > 0 ? ((numericValue / paymentTotal) * 100).toFixed(1) : '0.0';
                        return [formatCurrency(numericValue), `${name} (${percent}%)`];
                      }}
                      itemStyle={{ color: 'hsl(var(--foreground))' }}
                      labelStyle={{ color: 'hsl(var(--muted-foreground))' }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-muted-foreground">
                  No payment data yet
                </div>
              )}
            </div>
            {paymentTotal > 0 && (
              <div className="mt-3 grid grid-cols-1 gap-2">
                {paymentDistribution.map((item, index) => {
                  const percent = paymentTotal > 0 ? (item.value / paymentTotal) * 100 : 0;
                  return (
                    <div key={item.name} className="flex items-center justify-between gap-3 rounded-md bg-background/45 px-3 py-2">
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <span
                          className="inline-block h-2 w-2 rounded-full shrink-0"
                          style={{ backgroundColor: COLORS[index % COLORS.length] }}
                        />
                        <span className="text-sm leading-5 break-words text-foreground">{item.name}</span>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-sm font-semibold text-foreground">{percent.toFixed(1)}%</p>
                        <p className="text-xs text-muted-foreground">{formatCurrency(item.value)}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Survival Tips */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-warning" />
            Survival Tips
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {survivalTips.map((tip) => {
              const classes =
                tip.tone === 'critical'
                  ? 'bg-destructive/10 border-destructive/30'
                  : tip.tone === 'warning'
                    ? 'bg-warning/10 border-warning/30'
                    : tip.tone === 'positive'
                      ? 'bg-primary/10 border-primary/30'
                      : 'bg-muted/20';
              const titleClass =
                tip.tone === 'critical'
                  ? 'text-destructive'
                  : tip.tone === 'warning'
                    ? 'text-warning'
                    : tip.tone === 'positive'
                      ? 'text-primary'
                      : '';

              return (
                <div key={tip.id} className={`p-4 rounded-lg ${classes}`}>
                  <h4 className={`font-semibold mb-1 ${titleClass}`}>{tip.title}</h4>
                  <p className="text-sm text-muted-foreground">{tip.description}</p>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Footer */}
      <div className="text-center text-sm text-muted-foreground py-4">
        Powered by <span className="text-secondary font-medium">Kareem Data Labs</span> • 
        URA-Compliant POS for Ugandan SMEs
      </div>
    </div>
  );
};

export default DashboardPage;
